import { Alert, Box, Container, FormHelperText, Paper, Skeleton, Snackbar, Typography } from '@mui/material';
import { useEffect, useState, FC } from 'react';
import { useNavigate } from 'react-router';
import PaymentVendors from '@acorn-portal/shared/src/components/vendor/PaymentVendors';
import SimpleBraintreePaymentForm from '../../components/payment/simple-braintree-payment-form';
import PaymentHeader from '@acorn-portal/shared/src/components/header/PaymentHeader';
import { formatCurrency } from '@acorn-portal/shared/src/utils';
import { useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { useDispatch } from 'react-redux';
import { updatePayNowDetails } from '@acorn-portal/shared/src/store/paynow.slice';
import { PayNowClient } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { CustomPaymentMethodPayload } from '@acorn-portal/shared/src/utils/types/extensions/braintree';

const DebtCollectionPayment: FC = () => {
	const dispatch = useDispatch();
	const payNowDetails = useAppSelector((redux) => redux.payNow);
	const [errorMessage, setErrorMessage] = useState<string>();
	const [openError, setError] = useState(false);
	const [loading, setLoading] = useState(false);
	const [clientToken, setClientToken] = useState('');
	const navigate = useNavigate();
	const isVaulting = !payNowDetails.missingPaymentFormDetails.vaultCardOptOut;

	const getClientToken = async () => {
		setLoading(true);
		try {
			const client = new PayNowClient(undefined, API);
			const customerId = payNowDetails.details?.customerId;
			if (customerId) {
				const response = await client.generatePaymentToken(undefined);
				if (response) {
					setClientToken(response);
				} else {
					const responseNewPayment = await client.generatePaymentToken(undefined);
					setClientToken(responseNewPayment);
				}
			} else {
				const response = await client.generatePaymentToken(undefined);
				setClientToken(response);
			}
		} catch (e) {
			console.error('Error getting client token:', e);
			setErrorMessage('Unable to get payment token. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const onProcessPayment = async (payload: CustomPaymentMethodPayload) => {
		const client = new PayNowClient(undefined, API);
		const { nonce, threeDSecureInfo, deviceData } = payload;
		try {
			const response = await client.makeOutstandingDebtPayment({
				paymentNonce: nonce,
				amount: payNowDetails.details?.unaccountedForBalance,
				threeDSecureAuthenticationId: threeDSecureInfo ? threeDSecureInfo.threeDSecureAuthenticationId : '',
				emailAddress: payNowDetails.details?.email,
				deviceData,
				vaultCard: isVaulting,
				agreementId: payNowDetails.details?.agreementId,
			});
			if (response.isSuccessful && payNowDetails?.details) {
				const paymentDetails = await client.getPaymentDetails(payNowDetails.details.policyReference);
				dispatch(updatePayNowDetails({ details: paymentDetails }));
				navigate(ROUTE_PATHS.canxPaymentSuccess, {
					state: {
						amount: payNowDetails.details?.unaccountedForBalance,
					},
				});
			} else {
				setError(true);
			}
		} catch (e) {
			console.error('Error processing payment:', e);
			setError(true);
		}
	};

	useEffect(() => {
		if (!payNowDetails.details) {
			navigate(ROUTE_PATHS.home);
			return;
		}

		if (clientToken === '') {
			getClientToken();
		}
	}, [payNowDetails]);

	if (!payNowDetails.details) {
		navigate(ROUTE_PATHS.error);
		return null;
	}

	return (
		<Paper
			elevation={1}
			sx={{
				margin: 1.5,
				borderRadius: '8px',
				paddingBottom: 3,
			}}
		>
			<Container maxWidth='sm'>
				<Box sx={{ padding: 1 }} />
				<Box my={4}>
					<PaymentHeader title='Secure Payment Checkout' />
					<Typography textAlign='left' sx={{ fontSize: '20px' }}>
						Payment to be taken :{' '}
						<b>
							{payNowDetails?.details?.unaccountedForBalance
								? formatCurrency(payNowDetails?.details.unaccountedForBalance)
								: ''}
						</b>
					</Typography>
					<Typography textAlign='left'>You will receive an email as receipt and confirmation of payment.</Typography>
					{clientToken && payNowDetails ? (
						<SimpleBraintreePaymentForm
							loading={loading}
							amount={payNowDetails.details.unaccountedForBalance.toString()}
							clientToken={clientToken}
							submitPayment={onProcessPayment}
							isOptOut={!isVaulting}
						/>
					) : (
						<Skeleton variant='rectangular' width='100%' height={200} sx={{ mt: 5 }} />
					)}
					{errorMessage && <FormHelperText error>{errorMessage}</FormHelperText>}
					<PaymentVendors />
				</Box>
				<Snackbar open={openError} autoHideDuration={6000} onClose={() => setError(false)}>
					<Alert onClose={() => setError(false)} severity='error' sx={{ width: '100%' }}>
						Unable to process payment.
					</Alert>
				</Snackbar>
			</Container>
		</Paper>
	);
};
export default DebtCollectionPayment;
